import React, { useState } from 'react';
import './ElevatorDesign.css';

const lruCacheCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

template <typename K, typename V>
class LRUCache {
private:
    class Node {
    public:
        K key;
        V value;
        Node *prev;
        Node *next;

        Node(K k, V v) : key(k), value(v), prev(nullptr), next(nullptr) {}
    };

    int capacity = 0;
    Node *head, *tail;
    unordered_map<K, Node *> cacheMap;

    void addNode(Node *node) {
        cout << "  [LinkedList] Adding node with key: " << node->key << " to the front." << endl;
        Node *nextNode = head->next;
        node->next = nextNode;
        node->prev = head;
        head->next = node;
        nextNode->prev = node;
    }

    void removeNode(Node *node) {
        cout << "  [LinkedList] Removing node with key: " << node->key << " from its current position." << endl;
        Node *prevNode = node->prev;
        Node *nextNode = node->next;
        prevNode->next = nextNode;
        nextNode->prev = prevNode;
    }

public:
    LRUCache(int cap) {
        cout << "[Cache] Initializing cache with capacity: " << cap << endl;
        capacity = cap;
        head = new Node(K(), V());
        tail = new Node(K(), V());
        head->next = tail;
        tail->prev = head;
    }

    bool get(K key, V &val) {
        cout << "[Cache] GET request for key: " << key << endl;
        if (cacheMap.find(key) == cacheMap.end()) {
            cout << "  [Cache] Key " << key << " not found (Miss!)" << endl;
            return false;
        }

        Node *node = cacheMap[key];
        val = node->value;
        cout << "  [Cache] Key " << key << " found (Hit!). Value: " << val << endl;
        removeNode(node);
        addNode(node);
        return true;
    }

    void put(K key, V value) {
        cout << "[Cache] PUT request for key: " << key << ", value: " << value << endl;
        if (cacheMap.find(key) == cacheMap.end()) {
            if (cacheMap.size() == capacity) {
                Node *lastNode = tail->prev;
                K evictedKey = lastNode->key;
                cout << "  [Cache] Capacity full! Evicting LRU key: " << evictedKey << endl;
                removeNode(lastNode);
                cacheMap.erase(evictedKey);
                delete (lastNode);
            }

            cout << "  [Cache] Inserting new key: " << key << endl;
            Node *newNode = new Node(key, value);
            addNode(newNode);
            cacheMap[key] = newNode;
        } else {
            cout << "  [Cache] Key " << key << " already exists. Updating value to: " << value << endl;
            Node *node = cacheMap[key];
            node->value = value;
            removeNode(node);
            addNode(node);
        }
    }
};

int main() {
    cout << "--- Testing LRU Cache (Capacity: 2) ---" << endl;

    LRUCache<int, int> cache(2);
    int val = 0;

    cout << "\n>>> put(1, 10)" << endl;
    cache.put(1, 10);

    cout << "\n>>> put(2, 20)" << endl;
    cache.put(2, 20);

    cout << "\n>>> get(1)" << endl;
    if (cache.get(1, val)) {
        cout << "Result: " << val << " (Expected: 10)" << endl;
    }

    cout << "\n>>> put(3, 30) -> Should evict key 2" << endl;
    cache.put(3, 30);

    cout << "\n>>> get(2)" << endl;
    if (!cache.get(2, val)) {
        cout << "Result: failed. (Expected: Evicted successfully!)" << endl;
    }

    return 0;
}
`;

const cachePatterns = [
    'LRU (Least Recently Used): keeps the most recently accessed item near the front.',
    'LFU (Least Frequently Used): tracks access counts, evicting the item used least often.',
    'FIFO (First In, First Out): evicts the oldest data added to the cache first.',
    'TTL (Time-To-Live): automatically removes data after a fixed, predetermined period.'
];


const lfuCacheCode = String.raw`
#include <iostream>
#include <unordered_map>
#include <string>

using namespace std;

template <typename K, typename V>
class LFUCache {
private:
    class Node {
    public:
        K key;
        V value;
        int count;
        Node *prev, *next;

        Node(K k, V v) : key(k), value(v), count(1), prev(nullptr), next(nullptr) {}
    };

    class LinkedList {
    public:
        Node *head, *tail;
        int size;

        LinkedList() {
            head = new Node(K(), V());
            tail = new Node(K(), V());
            head->next = tail;
            tail->prev = head;
            size = 0;
        }

        ~LinkedList() {
            delete head;
            delete tail;
        }

        void removeNode(Node* node) {
            Node* prevNode = node->prev;
            Node* nextNode = node->next;
            prevNode->next = nextNode;
            nextNode->prev = prevNode;
            size--;
        }

        void addNode(Node* node) {
            Node* nextNode = head->next;
            head->next = node;
            node->prev = head;
            node->next = nextNode;
            nextNode->prev = node;
            size++;
        }

        Node* getLFUNode() {
            return tail->prev;
        }
    };

    int capacity;
    int minCount;

    unordered_map<K, Node*> cacheMap;
    unordered_map<int, LinkedList*> frequencyMap;

    void updateNodeCount(Node* node) {
        int presCount = node->count;

        LinkedList* oldList = frequencyMap[presCount];
        oldList->removeNode(node);

        if (presCount == minCount && oldList->size == 0) {
            minCount++;
        }

        if (oldList->size == 0) {
            frequencyMap.erase(presCount);
            delete oldList;
        }

        node->count++;
        if (!frequencyMap.count(node->count)) {
            frequencyMap[node->count] = new LinkedList();
        }
        frequencyMap[node->count]->addNode(node);
    }

public:
    LFUCache(int cap) : capacity(cap), minCount(0) {}

    ~LFUCache() {
        for (auto& pair : frequencyMap) {
            delete pair.second;
        }
        for (auto& pair : cacheMap) {
            delete pair.second;
        }
    }

    bool get(K key, V &value) {
        if (capacity == 0 || !cacheMap.count(key)) {
            cout << "  [GET] Key " << key << " -> MISS\n";
            return false;
        }

        Node* node = cacheMap[key];
        value = node->value;
        updateNodeCount(node);

        cout << "  [GET] Key " << key << " -> HIT! Value: " << value << " (New Freq: " << node->count << ")\n";
        return true;
    }

    void put(K key, V value) {
        if (capacity == 0) return;

        if (cacheMap.count(key)) {
            cout << "  [PUT] Key " << key << " exists. Updating value to " << value << "\n";
            Node* node = cacheMap[key];
            node->value = value;
            updateNodeCount(node);
            return;
        }

        if (cacheMap.size() == capacity) {
            LinkedList* minList = frequencyMap[minCount];
            Node* lfuNode = minList->getLFUNode();

            cout << "  [PUT] Cache Full! Evicting LFU Key: " << lfuNode->key << " (Freq: " << lfuNode->count << ")\n";

            cacheMap.erase(lfuNode->key);
            minList->removeNode(lfuNode);

            if (minList->size == 0) {
                frequencyMap.erase(minCount);
                delete minList;
            }
            delete lfuNode;
        }

        cout << "  [PUT] Inserting new Key: " << key << " with Value: " << value << "\n";
        Node* newNode = new Node(key, value);
        cacheMap[key] = newNode;

        if (!frequencyMap.count(1)) {
            frequencyMap[1] = new LinkedList();
        }
        frequencyMap[1]->addNode(newNode);

        minCount = 1;
    }
};

int main() {
    cout << "--- Testing O(1) Templated LFU Cache (Capacity: 2) ---\n";
    cout << "--- Using <int, string> mapping ---\n\n";

    LFUCache<int, string> cache(2);
    string val;

    cout << "1. Filling cache with Keys 1 and 2:\n";
    cache.put(1, "Apple");
    cache.put(2, "Banana");

    cout << "\n2. Accessing Key 1 twice (increasing its frequency to 3):\n";
    cache.get(1, val);
    cache.get(1, val);

    cout << "\n3. Inserting Key 3 (Triggers Eviction of Freq 1 item):\n";
    cache.put(3, "Cherry");

    cout << "\n4. Verifying Cache State:\n";
    cache.get(2, val);
    cache.get(1, val);

    cout << "\n5. Testing Tie-Breaking (LRU policy within same frequency group):\n";
    cache.put(4, "Date");

    cout << "\n6. Final State Checks:\n";
    cache.get(3, val);
    cache.get(4, val);

    cout << "\n--- All tests completed! ---\n";
    return 0;
}
`;

const stupidCacheCode = String.raw`
#include <bits/stdc++.h>
#include <chrono>
#include <thread>
using namespace std;

template <typename K, typename V>
class StupidCache {
private:
    class Node {
    public:
        K key;
        V value;
        Node *prev;
        Node *next;
        
        Node(K k, V v): key(k), value(v), prev(nullptr), next(nullptr) {}
    };

    int capacity = 1; // Strictly enforced. One is more than enough.
    Node* head;
    Node* tail;
    unordered_map<K, Node*> cacheMap;
  
    // We still use a doubly linked list just for the unnecessary overhead!
    void addNode(Node* node){
        Node* nextNode = head->next;
        node->next = nextNode;
        node->prev = head;
        head->next = node;
        nextNode->prev = node;
    }
  
    void removeNode(Node* node){
        Node *prevNode = node->prev;
        Node* nextNode = node->next;
        prevNode->next = nextNode;
        nextNode->prev = prevNode;
    }

    // The core feature of this cache
    void inflictPain() {
        cout << "  [Network] Simulating lag... please wait 500ms..." << endl;
        this_thread::sleep_for(chrono::milliseconds(500));
    }

public:
    StupidCache() {
        cout << "[StupidCache] Booting up. Capacity strictly limited to 1." << endl;
        head = new Node(K(), V());
        tail = new Node(K(), V());
        head->next = tail;
        tail->prev = head;
    }
    
    // Always returns false. Always.
    bool get(K key, V &val) {
        cout << "\n[StupidCache] GET request for key: " << key << endl;
        inflictPain(); // Guarantee the latency experience
        
        if (cacheMap.find(key) != cacheMap.end()) {
            cout << "  [StupidCache] Wow, a cache HIT! Key " << key << " is actually here!" << endl;
            
            // Immediately evict it on access. 
            Node *node = cacheMap[key];
            removeNode(node);
            cacheMap.erase(key);
            delete node;
            
            cout << "  [StupidCache] ...but I deleted it. Go fetch it from the database yourself." << endl;
        } else {
            cout << "  [StupidCache] Cache MISS." << endl;
        }
        
        // Reject all cache hits
        return false; 
    }
    
    void put(K key, V value) {
        cout << "\n[StupidCache] PUT request for key: " << key << ", value: " << value << endl;
        inflictPain(); // Put requests deserve lag too
        
        // If there's literally anything in the cache, evict it to maintain capacity of 1
        if (!cacheMap.empty()) {
            Node *lastNode = tail->prev;
            K evictedKey = lastNode->key; 
            
            cout << "  [StupidCache] Cache full (size = 1)! Evicting key: " << evictedKey << endl;
            
            removeNode(lastNode);
            cacheMap.erase(evictedKey);
            delete lastNode;
        }
        
        cout << "  [StupidCache] Storing key: " << key << " (It will be deleted the moment you ask for it)" << endl;
        Node *newNode = new Node(key, value);
        addNode(newNode);
        cacheMap[key] = newNode;
    }
};

int main() {
    StupidCache<int, string> cache;
    string val = "";

    // 1. Put something in
    cache.put(1, "User Data");

    // 2. Try to get it (It exists, but the cache will delete it and return false)
    if (!cache.get(1, val)) {
        cout << ">>> Caller: Fine, I'll go query the database." << endl;
        // Simulating the user fetching from DB and putting it back
        cache.put(1, "User Data");
    }

    // 3. Put a new item in (Instantly evicts key 1 because capacity is 1)
    cache.put(2, "Settings");

    // 4. Try to get key 1 again
    cache.get(1, val);

    return 0;
}
`;

function CacheDesign({ theme, onBackClick }) {
    const [copied, setCopied] = useState({
        lru: false,
        lfu: false,
        stupid: false,
    });

    const handleCopyCode = async (codeKey, codeText) => {
        try {
            await navigator.clipboard.writeText(codeText);
            setCopied((prev) => ({ ...prev, [codeKey]: true }));
            window.setTimeout(() => {
                setCopied((prev) => ({ ...prev, [codeKey]: false }));
            }, 1800);
        } catch (error) {
            console.error('Copy failed:', error);
            setCopied((prev) => ({ ...prev, [codeKey]: false }));
        }
    };

    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Cache Design</h1>
                            <p className="system-designs-subtitle">
                                A cache is just a tiny notepad to quickly lookup the most used/recent data.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Designs
                        </button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Functional Requirements</h2>
                            <ul>
                                <li>Store values for frequent reads without making the backing store do all the work.</li>
                                <li>Serve a fast hit lookup for common keys and a fast miss path for new ones.</li>
                                <li>Evict stale or low-value entries when capacity is reached.</li>
                                <li>Support updates to existing keys without duplicating state or corrupting ordering.</li>
                                <li>Keep the cache logical enough to explain to a teammate without a whiteboard and three coffees (..adds smiley face) </li>
                                <li>Handle high read rates while still preserving some sense of predictability.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Design</h2>
                            <ul>
                                <li>Use a hash map for O(1) key lookup and a doubly linked list for eviction ordering.</li>
                                <li>Head of the list is the most recently used item, tail is the least recently used item.</li>
                                <li>On a GET call, move the accessed node to the front and return the value.</li>
                                <li>On a PUT call for a new key, insert at the front and evict the tail if capacity is full.</li>
                                <li>On a PUT call for an existing key, update the value and move the node to the front.</li>
                                <li>The system can be extended with TTLs, async refreshes, and shard-level partitioning for larger workloads.</li>
                                <li>A good implementation separates eviction policy, metadata, and storage logic so the whole thing does not become a single pile of mystery pointers.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Design</h2>
                            <ul>
                                <li>Keep cache size to 1 entry and evict it on every access, even if the same key is requested repeatedly.</li>
                                <li>Reject all cache hits and always fetch from the source, just to make sure the user experiences the full latency of a network request.</li>
                                <li>Use a SPECIAL cache that only stores the most recently accessed item and immediately evicts it on any access.</li>
                                <li>Or...for the real latency experience, just add a 500ms delay to every call(regardless of hit/miss).</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Main Entities</h2>
                            <ul>
                                <li>Hash map: key to node pointer</li>
                                <li>Doubly linked list: ordering by recency</li>
                                <li>Node: key, value, prev, next</li>
                                <li>Capacity: maximum number of entries before eviction</li>
                                <li>GET/PUT: the public cache API</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Common Cache Eviction Patterns</h2>
                            <ul>
                                {cachePatterns.map((entry) => (
                                    <li key={entry}>{entry}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>LRU Cache sample:</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('lru', lruCacheCode)}>
                                    {copied.lru ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{lruCacheCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>LFU Cache sample:</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('lfu', lfuCacheCode)}>
                                    {copied.lfu ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{lfuCacheCode}</code></pre>
                        </div>

                        <div className="design-section stupid-design-section">
                            <div className="design-code-header">
                                <h2>Stupid Cache</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('stupid', stupidCacheCode)}>
                                    {copied.stupid ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{stupidCacheCode}</code></pre>
                        </div>

                    </div>

                    <div className="system-designs-footer-note">
                        Try it out, break it, then call it a feature.
                    </div>

                    <button className="back-btn" onClick={onBackClick}>
                        Back to Designs
                    </button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default CacheDesign;
